import { Check } from 'lucide-react';
import type { PickemResponse } from '@/test/fixtures/api-fixtures';

interface PickemTimelineStepperProps {
  pickem: PickemResponse;
  allQuestionsHaveCorrectAnswer: boolean;
  questionsCount: number;
}

export function PickemTimelineStepper({
  pickem,
  allQuestionsHaveCorrectAnswer,
  questionsCount,
}: PickemTimelineStepperProps) {
  const openDate = pickem.allow_selection_start ? new Date(pickem.allow_selection_start) : null;
  const startDate = pickem.start ? new Date(pickem.start) : null;

  // Use explicit status for step logic
  const pickemStatus = pickem.status || (pickem as any).pickem_status || '';
  const isScheduled = pickemStatus === 'SCHEDULED';
  const isOpen =
    pickemStatus === 'OPEN' || pickemStatus === 'INPLAY' || pickemStatus === 'COMPLETE';
  const isInPlay = pickemStatus === 'INPLAY' || pickemStatus === 'COMPLETE';
  const isComplete = pickemStatus === 'COMPLETE';

  // Step 1: Scheduled
  const step1Complete = isScheduled || isOpen || isInPlay || isComplete;
  const step1Current = !step1Complete;

  // Step 2: Open
  const step2Complete = isOpen;
  const step2Current = isScheduled && !isOpen;

  // Step 3a: InPlay
  const step3aComplete = isInPlay;
  const step3aCurrent = isOpen && !isInPlay && !isComplete;

  // Step 3b: Questions Answered
  const step3bComplete = allQuestionsHaveCorrectAnswer;
  const step3bCurrent = isInPlay && !allQuestionsHaveCorrectAnswer && !isComplete;

  // Step 4: Paid Out / Complete
  const step4Complete = isComplete;
  const step4Current = isInPlay && allQuestionsHaveCorrectAnswer && !isComplete;

  const getStepColor = (isComplete: boolean, isCurrent: boolean) => {
    if (isComplete) return 'bg-green-500';
    if (isCurrent) return 'bg-blue-500';
    return 'bg-gray-300';
  };

  const getLineColor = (isComplete: boolean) => {
    return isComplete ? 'bg-green-500' : 'bg-gray-300';
  };

  return (
    <div className="w-full py-6 mb-6">
      <div className="flex items-start">
        {/* Step 1: Scheduled */}
        <div className="flex flex-col items-center flex-shrink-0">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white transition-colors ${getStepColor(step1Complete, step1Current)}`}
          >
            {step1Complete ? <Check className="w-5 h-5" /> : <span className="text-sm">1</span>}
          </div>
          <div className="mt-2 text-center">
            <div className="text-sm font-medium text-gray-900">Scheduled</div>
            {openDate && (
              <div className="text-xs text-gray-500 mt-0.5">{openDate.toLocaleDateString()}</div>
            )}
          </div>
        </div>

        {/* Line to Step 2 */}
        <div className="flex-1 flex items-start pt-5">
          <div className={`h-0.5 w-full transition-colors ${getLineColor(step2Complete)}`} />
        </div>

        {/* Step 2: Open */}
        <div className="flex flex-col items-center flex-shrink-0">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white transition-colors ${getStepColor(step2Complete, step2Current)}`}
          >
            {step2Complete ? <Check className="w-5 h-5" /> : <span className="text-sm">2</span>}
          </div>
          <div className="mt-2 text-center">
            <div className="text-sm font-medium text-gray-900">Open</div>
            {openDate && (
              <div className="text-xs text-gray-500 mt-0.5">{openDate.toLocaleDateString()}</div>
            )}
          </div>
        </div>

        {/* Split section: Line splits into two branches */}
        <div className="flex-1 flex flex-col items-center justify-center pt-5 px-4 relative">
          {/* Horizontal line from step 2 */}
          <div className="absolute left-0 right-1/2 top-5">
            <div
              className={`h-0.5 w-full transition-colors ${getLineColor(step3aComplete || step3bComplete)}`}
            />
          </div>

          {/* Upper branch to 3a */}
          <div className="w-full flex items-start mb-8">
            <div className="w-1/2 flex justify-end pr-2">
              <div className={`h-0.5 w-full transition-colors ${getLineColor(step3aComplete)}`} />
            </div>
            <div className="flex flex-col items-center flex-shrink-0 -mt-5">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-white text-xs transition-colors ${getStepColor(step3aComplete, step3aCurrent)}`}
              >
                {step3aComplete ? <Check className="w-4 h-4" /> : <span>3a</span>}
              </div>
              <div className="mt-2 text-center whitespace-nowrap">
                <div className="text-xs font-medium text-gray-900">InPlay</div>
                {startDate && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {startDate.toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lower branch to 3b */}
          <div className="w-full flex items-start">
            <div className="w-1/2 flex justify-end pr-2">
              <div className={`h-0.5 w-full transition-colors ${getLineColor(step3bComplete)}`} />
            </div>
            <div className="flex flex-col items-center flex-shrink-0 -mt-5">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-white text-xs transition-colors ${getStepColor(step3bComplete, step3bCurrent)}`}
              >
                {step3bComplete ? <Check className="w-4 h-4" /> : <span>3b</span>}
              </div>
              <div className="mt-2 text-center whitespace-nowrap">
                <div className="text-xs font-medium text-gray-900">Answered</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {questionsCount} question{questionsCount !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Lines converging from 3a and 3b to step 4 */}
          <div className="absolute left-1/2 right-0 top-5">
            <div
              className={`h-0.5 w-full transition-colors ${getLineColor(step3aComplete && step3bComplete)}`}
            />
          </div>
        </div>

        {/* Step 4: Scored & Complete */}
        <div className="flex flex-col items-center flex-shrink-0">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white transition-colors ${getStepColor(step4Complete, step4Current)}`}
          >
            {step4Complete ? <Check className="w-5 h-5" /> : <span className="text-sm">4</span>}
          </div>
          <div className="mt-2 text-center">
            <div className="text-sm font-medium text-gray-900">Paid Out</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {step4Complete ? 'Completed' : 'Pending'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
